import { filterContactInput, contactErrors } from "../../../../utils/contact-validation.ts";
import { useState, type FormEvent } from "react";
import { Button, TextArea, TextField } from "../../../ui/index.ts";
import { WizardRailLayout } from "../../onboarding-shared/wizard-rail-layout.tsx";
import { OnboardingDocumentUploadField } from "../shared/components/document-upload-field.tsx";
import { bankDetailsSchema, EMPTY_VALUES, personalDetailsSchema, type ConsultantIndiaValues } from "./consultant-india.schema.ts";
import { useOnboardingSubmission } from "../shared/use-onboarding-submission.ts";
import { mapIndiaConsultant } from "../shared/submission-mappers.ts";
import { showError } from "../../../../utils/toast.ts";
import { W9_DOWNLOAD_URL } from "../consultant-us/consultant-us.config.ts";
import styles from "./consultant-india-form.module.css";

const STEPS = [
  { id: "personal", label: "Personal Details" },
  { id: "bank", label: "Bank Details" },
  { id: "review", label: "Review" },
];

const PERSONAL_FIELDS = [
  { key: "firstName", label: "First Name", required: true },
  { key: "lastName", label: "Last Name", required: true },
  { key: "contactNumber", label: "Contact Number", required: true },
  { key: "alternateContactNumber", label: "Alternate Contact Number", required: false },
] as const;

const BANK_FIELDS = [
  { key: "accountName", label: "Account Name" },
  { key: "accountNumber", label: "Account Number" },
  { key: "pan", label: "Corresponding PAN" },
  { key: "bankName", label: "Bank Name" },
  { key: "branchName", label: "Branch Name" },
  { key: "ifscCode", label: "IFSC Code" },
] as const;

function validateCheque(file: File): string | undefined {
  if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
    return "Choose an image or PDF file.";
  }
  if (!file.size) return "The selected file is empty.";
  if (file.size > 5 * 1024 * 1024) return "Choose a file smaller than 5 MB.";
  return undefined;
}

export function ConsultantIndiaForm({ onSubmitted }: { onSubmitted?: () => void }) {
  const submission = useOnboardingSubmission("consultant");
  const [tax, setTax] = useState<File | null>(null);
  const [step, setStep] = useState(0);
  const [values, setValues] = useState<ConsultantIndiaValues>(() => ({ ...EMPTY_VALUES, firstName: submission.verified?.firstName ?? "", lastName: submission.verified?.lastName ?? "" }));
  const [errors, setErrors] = useState<Partial<Record<keyof ConsultantIndiaValues, string>>>({});
  const [cheque, setCheque] = useState<File | null>(null);

  const update = (key: keyof ConsultantIndiaValues, value: string) => {
    value = filterContactInput(key, value);
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const next = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submission.busy || submission.complete) return;
    const invalidContacts = contactErrors(values);
    if (Object.keys(invalidContacts).length) { setErrors(invalidContacts); setStep(Object.keys(invalidContacts).some((key) => key !== "bankName") ? 0 : 1); return; }
    const result = (step === 0 ? personalDetailsSchema : step === 1 ? bankDetailsSchema : personalDetailsSchema.merge(bankDetailsSchema)).safeParse(values);
    if (!result.success) {
      const nextErrors: typeof errors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof ConsultantIndiaValues;
        nextErrors[key] ??= issue.message;
      }
      setErrors(nextErrors);
      const firstKey = result.error.issues[0]?.path[0];
      document.getElementById(`consultant-india-${firstKey}`)?.focus();
      return;
    }
    setValues((current) => ({ ...current, ...result.data }));
    setErrors({});
    if (step < 2) setStep((current) => current + 1);
    else if (
      await submission.submit(
        mapIndiaConsultant(values),
        { cheque: cheque ?? undefined },
      )
    ) {
      onSubmitted?.();
    }
  };

  return (
    <WizardRailLayout
      steps={STEPS}
      currentStepIndex={step}
      sectionTitle={STEPS[step].label}
      onBack={submission.busy || submission.complete ? undefined : () => { setErrors({}); setStep((current) => Math.max(0, current - 1)); }}
      primaryLabel={submission.complete ? "Submitted" : step === 2 ? "Submit" : step === 1 ? "Review Details" : "Continue"}
      primaryDisabled={submission.busy || submission.complete}
      primaryLoading={submission.busy}
      primaryFormId="consultant-india-form"
    >
      <form id="consultant-india-form" onSubmit={next} noValidate>
        <fieldset disabled={submission.busy || submission.complete} className={styles.formFields}>
          {step < 2 ? (
            <div>
              <div className={styles.grid}>
                {step === 0 ? (
                  <>
                    {PERSONAL_FIELDS.map(({ key, label, required }) => (
                      <TextField
                        key={key}
                        id={`consultant-india-${key}`}
                        label={label}
                        required={required}
                        type={(key === "firstName" || key === "lastName") ? "text" : "tel"}
                        inputMode={(key === "firstName" || key === "lastName") ? "text" : "numeric"}
                        maxLength={(key === "firstName" || key === "lastName") ? 100 : 10}
                        placeholder={`Enter ${label}`}
                        value={values[key]}
                        error={errors[key]}
                        onChange={(event) => update(key, (key === "firstName" || key === "lastName") ? event.target.value : event.target.value.replace(/\D/g, ""))}
                      />
                    ))}
                    <div className={styles.fullWidth}>
                      <TextArea
                        id="consultant-india-correspondenceAddress"
                        label="Correspondence Address"
                        required
                        rows={3}
                        maxLength={500}
                        placeholder="Enter Correspondence Address"
                        value={values.correspondenceAddress}
                        error={errors.correspondenceAddress}
                        onChange={(event) => update("correspondenceAddress", event.target.value)}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {BANK_FIELDS.map(({ key, label }) => (
                      <TextField
                        key={key}
                        id={`consultant-india-${key}`}
                        label={label}
                        required={key !== "branchName"}
                        autoComplete="off"
                        inputMode={key === "accountNumber" ? "numeric" : "text"}
                        maxLength={
                          key === "pan"
                            ? 10
                            : key === "ifscCode"
                              ? 11
                              : key === "accountNumber"
                                ? 18
                                : 100
                        } placeholder={`Enter ${label}`}
                        value={values[key]}
                        error={errors[key]}
                        onChange={(event) => {
                          const value = event.target.value;

                          update(
                            key,
                            key === "accountNumber"
                              ? value.replace(/\D/g, "")
                              : key === "bankName" || key === "accountName"
                                ? value.replace(/[0-9]/g, "")
                                : key === "pan" || key === "ifscCode"
                                  ? value.toUpperCase()
                                  : value
                          );
                        }}
                      />
                    ))}
                    <div className={styles.fullWidth}>

                      <OnboardingDocumentUploadField
                        id="consultant-india-cheque"
                        label="Cancelled Cheque"
                        file={cheque}
                        validateFile={validateCheque}
                        onChange={setCheque}
                      />
                      <p className={styles.lead}>Image or PDF, up to 5 MB.</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div>
              <p className="sub-heading-lg mb-3">Review your details before submitting.</p>
              {[
                { title: "Personal Details", fields: [...PERSONAL_FIELDS, { key: "correspondenceAddress", label: "Correspondence Address" }] },
                { title: "Bank Details", fields: BANK_FIELDS },
              ].map(({ title, fields }) => (
                <section key={title}>
                  <h3 className="heading-sm mb-3">{title}</h3>
                  <dl className={styles.grid}>
                    {fields.map(({ key, label }) => (
                      <div key={key} className={styles.detail}>
                        <dt className="text-accent body-sm">{label}</dt>
                        <dd className="body-sm">{values[key as keyof ConsultantIndiaValues] || "Not provided"}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ))}
              <div className={styles.detail}><dt className="text-accent body-sm">Cancelled Cheque</dt><dd className="body-sm">{cheque?.name || "Not attached"}</dd></div>
            </div>
          )}
        </fieldset>
        {submission.complete && <p role="status">Your onboarding form was submitted successfully.</p>}
      </form>
    </WizardRailLayout>
  );
}
